//----------------------------------------------------------------------------------------------------------------------
// Keyboard Input Resource Access
//----------------------------------------------------------------------------------------------------------------------

// Interfaces
import type { KeyboardDevice, KeyboardInputState } from '../../interfaces/input.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Callback type for keyboard device connection events
 */
export type KeyboardDeviceCallback = (device : KeyboardDevice) => void;

/**
 * Callback type for keyboard input events
 */
export type KeyboardInputCallback = (device : KeyboardDevice, state : KeyboardInputState) => void;

/**
 * Responsible for tracking keyboard state and notifying subscribers of changes.
 */
export class KeyboardInputPlugin
{
    private _keyboardDevice : KeyboardDevice;
    private _keysState : Record<string, boolean> = {};

    // Callbacks
    private _onDeviceConnected ?: KeyboardDeviceCallback;
    private _onInputChanged ?: KeyboardInputCallback;

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Create a new KeyboardInputPlugin
     */
    constructor()
    {
        // Initialize keyboard device
        this._keyboardDevice = {
            id: 'keyboard-0',
            name: 'Keyboard',
            type: 'keyboard',
            connected: true,
        };

        // Set up event listeners
        this._setupKeyboardEvents();

        // Notify that device is connected (on next tick to allow callback registration)
        setTimeout(() => this._notifyDeviceConnected(), 0);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Register a callback for device connected events
     *
     * @param callback
     */
    public onDeviceConnected(callback : KeyboardDeviceCallback) : void
    {
        this._onDeviceConnected = callback;
    }

    /**
     * Register a callback for input changed events
     *
     * @param callback
     */
    public onInputChanged(callback : KeyboardInputCallback) : void
    {
        this._onInputChanged = callback;
    }

    /**
     * Get the current keyboard state
     */
    public getState() : KeyboardInputState
    {
        return {
            type: 'keyboard',
            keys: { ...this._keysState },
            delta: {},
        };
    }

    /**
     * Get the keyboard device
     */
    public getDevice() : KeyboardDevice
    {
        return { ...this._keyboardDevice };
    }

    /**
     * Tears down the keyboard resource access and cleans up event listeners
     */
    public $teardown() : Promise<void>
    {
        // Remove keyboard event listeners
        window.removeEventListener('keydown', this._handleKeyDown);
        window.removeEventListener('keyup', this._handleKeyUp);

        return Promise.resolve();
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Set up keyboard event listeners
     */
    private _setupKeyboardEvents() : void
    {
        // Bind handlers to this instance
        this._handleKeyDown = this._handleKeyDown.bind(this);
        this._handleKeyUp = this._handleKeyUp.bind(this);

        // Add event listeners
        window.addEventListener('keydown', this._handleKeyDown);
        window.addEventListener('keyup', this._handleKeyUp);
    }

    /**
     * Handle keyboard key down events
     */
    private _handleKeyDown(event : KeyboardEvent) : void
    {
        this._keysState[event.code] = true;

        const delta : Record<string, boolean> = {};
        delta[event.code] = true;

        const state : KeyboardInputState = {
            type: 'keyboard',
            keys: { ...this._keysState },
            delta,
            event,
        };

        this._notifyInputChanged(state);
    }

    /**
     * Handle keyboard key up events
     */
    private _handleKeyUp(event : KeyboardEvent) : void
    {
        this._keysState[event.code] = false;

        const delta : Record<string, boolean> = {};
        delta[event.code] = false;

        const state : KeyboardInputState = {
            type: 'keyboard',
            keys: { ...this._keysState },
            delta,
            event,
        };

        this._notifyInputChanged(state);
    }

    /**
     * Notify subscribers of device connected event
     */
    private _notifyDeviceConnected() : void
    {
        if(this._onDeviceConnected)
        {
            this._onDeviceConnected(this._keyboardDevice);
        }
    }

    /**
     * Notify subscribers of input changed event
     */
    private _notifyInputChanged(state : KeyboardInputState) : void
    {
        if(this._onInputChanged)
        {
            this._onInputChanged(this._keyboardDevice, state);
        }
    }
}

//----------------------------------------------------------------------------------------------------------------------
